/**
 * Adónde lleva cada alerta del Resumen y la lista «Para empezar a enviar». Puro.
 *
 * La ruta sale del `enlace` que manda el backend (tipo de lista cerrada, SPEC-backend §13.1); el
 * texto del botón, del código. La tabla por código es el respaldo si llega un código nuevo sin un
 * enlace utilizable: una alerta nunca queda sin salida.
 */

import type { AlertaResumen, CuentaPublica, EnlaceAlerta, ModoTransporte } from "../types";
import { cuentaDelModo } from "./conexion";

const R = "/activa/oficial";
const CONFIG = `${R}/configuracion`;

const POR_CODIGO: Record<string, { label: string; ruta: string }> = {
  plantilla_pausada: { label: "Ver plantilla", ruta: `${R}/plantillas?estado=frenadas` },
  plantilla_deshabilitada: { label: "Ver plantilla", ruta: `${R}/plantillas?estado=frenadas` },
  plantilla_48h: { label: "Ver plantilla", ruta: `${R}/plantillas?estado=revision` },
  recategorizada: { label: "Ver plantilla", ruta: `${R}/plantillas?estado=frenadas` },
  sin_callbacks_1h: { label: "Revisar webhooks", ruta: `${CONFIG}?seccion=cuenta` },
  event_streams_error: { label: "Revisar webhooks", ruta: `${CONFIG}?seccion=cuenta` },
  sender_inactivo_25d: { label: "Ver número", ruta: `${CONFIG}?seccion=cuenta` },
  sender_offline: { label: "Ver número", ruta: `${CONFIG}?seccion=cuenta` },
  saldo_bajo: { label: "Cargar saldo", ruta: `${CONFIG}?seccion=saldo` },
  tope_80: { label: "Ver gasto", ruta: `${CONFIG}?seccion=saldo` },
  tope_alcanzado: { label: "Ver gasto", ruta: `${CONFIG}?seccion=saldo` },
  marca_frenada: { label: "Ver por qué", ruta: `${CONFIG}?seccion=cuenta` },
  advanced_opt_out: { label: "Confirmar opt-out", ruta: `${CONFIG}?seccion=bajas` },
  // Alertas de la cuenta de Twilio (SPEC-estadisticas-conexion §B.3.6). Desde la etapa 2 el saldo de Twilio y
  // sus alertas de gasto viven en Conexión (técnico) (`SeccionCuenta`), no en «Saldo y costos», que es la
  // plata de la marca: mandarlas ahí mostraba el otro saldo, la confusión que la separación vino a sacar.
  // El botón está en el Inicio, que lee el comercio: dice «el proveedor», no «Twilio» (no sabe qué es).
  saldo_twilio_bajo: { label: "Ver saldo del proveedor", ruta: `${CONFIG}?seccion=cuenta` },
  // El saldo del proveedor no alcanza para lo que cargaron las marcas: ahí, «Saldo en Twilio» dice cuánto falta y cómo se
  // carga (una recarga en la plataforma no carga al proveedor).
  cobertura_twilio: { label: "Ver saldo del proveedor", ruta: `${CONFIG}?seccion=cuenta` },
  gasto_twilio_diario: { label: "Ver gasto del proveedor", ruta: `${CONFIG}?seccion=cuenta` },
  gasto_twilio_mensual: { label: "Ver gasto del proveedor", ruta: `${CONFIG}?seccion=cuenta` },
  calidad_media: { label: "Ver número", ruta: `${CONFIG}?seccion=cuenta` },
  calidad_baja: { label: "Ver número", ruta: `${CONFIG}?seccion=cuenta` },
  sender_actualizando: { label: "Ver número", ruta: `${CONFIG}?seccion=cuenta` },
  plantillas_otra_cuenta: { label: "Revisar conexión", ruta: `${CONFIG}?seccion=cuenta` },
  diagnostico_con_errores: { label: "Revisar conexión", ruta: `${CONFIG}?seccion=cuenta` },
  pedido_de_alta: { label: "Ver pedidos de alta", ruta: `${R}/bajas` },
  // Una plantilla creada desde el asistente se aprobó o se rechazó (SPEC-BD B-11): se vuelve al paso
  // Plantilla de ESE borrador (ver `rutaPorEnlace`), no a su detalle, que no deja seguir.
  borrador_plantilla_aprobada: { label: "Seguir con la campaña", ruta: `${R}/campanas` },
  borrador_plantilla_rechazada: { label: "Corregir", ruta: `${R}/campanas` },
  // El borrador real apunta a una plantilla del modo simulado (o de otra cuenta): se cambia en el mismo paso.
  borrador_plantilla_simulada: { label: "Cambiar plantilla", ruta: `${R}/campanas` },
};

/**
 * Las alertas que repiten un paso pendiente de «Para empezar a enviar». Con la marca recién conectada,
 * el Inicio decía dos veces lo mismo: el paso «Cargá saldo» y, abajo, «Queda poco saldo (USD 0,00)».
 * Mientras el paso está a la vista, la alerta sobra; cuando el paso se cumple, la alerta vuelve (saldo
 * bajo pero no cero, por ejemplo).
 */
const ALERTA_DEL_PASO: Partial<Record<PasoPuestaEnMarcha["clave"], string>> = {
  saldo: "saldo_bajo",
  optout: "advanced_opt_out",
};

export function sinRepetirLosPasos(alertas: AlertaResumen[], pasos: PasoPuestaEnMarcha[]): AlertaResumen[] {
  const repetidas = new Set(pasos.filter((p) => !p.hecho).map((p) => ALERTA_DEL_PASO[p.clave]).filter(Boolean));
  return alertas.filter((a) => !repetidas.has(a.codigo));
}

/** Alertas de un borrador que tienen que abrir el asistente en el paso Plantilla. */
const AL_ASISTENTE = new Set(["borrador_plantilla_aprobada", "borrador_plantilla_rechazada", "borrador_plantilla_simulada"]);

function rutaPorEnlace(e: EnlaceAlerta | undefined, codigo: string): string | null {
  if (!e || typeof e !== "object") return null;
  const id = typeof e.id === "string" && e.id ? encodeURIComponent(e.id) : "";
  const respaldo = POR_CODIGO[codigo]?.ruta;
  switch (e.tipo) {
    case "plantilla":
      return id ? `${R}/plantillas/${id}` : codigo === "plantilla_48h" ? `${R}/plantillas?estado=revision` : `${R}/plantillas?estado=frenadas`;
    case "campana":
      if (id && AL_ASISTENTE.has(codigo)) return `${R}/campanas/${id}/editar?paso=mensaje`;
      return id ? `${R}/campanas/${id}` : `${R}/campanas`;
    case "saldo":
      // El de la marca, salvo que la alerta sea de Twilio (un backend anterior a la etapa 2 manda `saldo`).
      return respaldo && respaldo.startsWith(CONFIG) ? respaldo : `${CONFIG}?seccion=saldo`;
    case "cuenta":
      return `${CONFIG}?seccion=cuenta`;
    case "config":
      // qué sección depende de la alerta (el opt-out vive en Bajas); sin dato, Cuenta
      return respaldo && respaldo.startsWith(CONFIG) ? respaldo : `${CONFIG}?seccion=cuenta`;
    case "bajas":
      // El backend manda `bajas` para `advanced_opt_out` (SPEC-backend §13.1), pero la tarjeta que
      // confirma la baja automática vive en Configuración → Bajas, no en «Bajas y consentimientos»:
      // si el código tiene su sección de Bajas en Configuración, manda el código.
      return respaldo && respaldo.startsWith(`${CONFIG}?seccion=bajas`) ? respaldo : `${R}/bajas`;
    default:
      return null;
  }
}

/** Ruta y texto del botón de una alerta del Resumen. */
export function destinoAlerta(a: AlertaResumen): { label: string; ruta: string } {
  const codigo = String(a?.codigo || "");
  const porCodigo = POR_CODIGO[codigo];
  const ruta = rutaPorEnlace(a?.enlace, codigo) ?? porCodigo?.ruta ?? R;
  return { label: porCodigo?.label ?? "Ver", ruta };
}

/**
 * Adónde lleva una alerta a una persona de un cliente (Accesos, rol «comercio»), o null si lo que pide lo hace el
 * equipo de Deenex: la conexión con Twilio, el número, los webhooks, la baja automática (todo en Configuración,
 * menos el saldo) y seguir armando un borrador en el asistente. Del saldo ve el suyo pero no lo carga: «Ver saldo».
 */
export function destinoAlertaParaCliente(a: AlertaResumen): { label: string; ruta: string } | null {
  const d = destinoAlerta(a);
  if (d.ruta.startsWith(`${CONFIG}?seccion=saldo`)) return { label: d.label === "Cargar saldo" ? "Ver saldo" : d.label, ruta: d.ruta };
  if (d.ruta.startsWith(CONFIG)) return null;
  if (/\/editar(\?|$)/.test(d.ruta)) return null;
  return d;
}

/**
 * Las alertas del Inicio que son del equipo de Deenex (el mismo criterio que `ALERTAS_DEL_EQUIPO` del backend, en
 * `modules/accesos/respuesta-comercio.ts`, que ya no se las manda a un cliente: esto cubre un backend anterior).
 * Una persona de un cliente no las ve:
 * - la cuenta de Twilio de Deenex: su saldo, si alcanza para lo que cargaron las marcas (`cobertura_twilio`: con
 *   subcuentas suma la plata de otros comercios) y los topes de gasto que le puso (Usage Triggers). Su mensaje dice
 *   cuánta plata tiene o gastó Deenex con el proveedor («El saldo de la cuenta del proveedor de WhatsApp es USD 7,50»),
 *   que no es la del comercio;
 * - las que nombran a Twilio y piden algo que hace el equipo: los recibos que no llegan («revisá los webhooks»), el
 *   aviso de aprobaciones (Event Streams), la baja automática del Messaging Service y el ticket en Twilio de una
 *   plantilla con más de 48 h en revisión.
 */
export const ALERTAS_DEL_EQUIPO: ReadonlySet<string> = new Set([
  "saldo_twilio_bajo", "cobertura_twilio", "gasto_twilio_diario", "gasto_twilio_mensual",
  "sin_callbacks_1h", "event_streams_error", "advanced_opt_out", "plantilla_48h", "sender_inactivo_25d",
]);

/** Las alertas del Inicio que puede ver una persona de un cliente (Accesos, rol «comercio»). */
export function alertasParaElComercio(alertas: AlertaResumen[]): AlertaResumen[] {
  return alertas.filter((a) => !ALERTAS_DEL_EQUIPO.has(String(a?.codigo || "")));
}

/**
 * Las alertas que se sacan porque repiten un paso de «Para empezar a enviar». Un cliente no ve esa lista (es la
 * puesta en marcha, la hace el equipo): para él no se saca ninguna, o «Queda poco saldo» desaparecería sin aviso.
 */
export function alertasSinRepetir(alertas: AlertaResumen[], pasos: PasoPuestaEnMarcha[], equipo: boolean): AlertaResumen[] {
  return equipo ? sinRepetirLosPasos(alertas, pasos) : alertas;
}

export interface PasoPuestaEnMarcha {
  clave: "comercio" | "cuenta" | "webhooks" | "optout" | "saldo" | "plantilla" | "campana"; titulo: string; hecho: boolean; ruta: string;
  /** Lo hace el equipo de Deenex (conectar con Twilio, webhooks, la baja automática): la marca no tiene cómo. */
  deDeenex?: boolean;
}

/**
 * Checklist del Resumen. En simulado no aplican webhooks ni opt-out (no hay Twilio del otro lado).
 * `cuenta` es la CuentaPublica (virtual con id:null si no hay); null = no se pudo traer, y entonces
 * nada cuenta como hecho. La cuenta cuenta como conectada con la regla del backend (`cuentaDelModo`):
 * una verificada en simulado no tacha «Conectá la cuenta» con el servidor en twilio.
 * `plantillasAprobadas` son las que sirven con la cuenta: en twilio el backend ya saca de
 * `porEstado` las simuladas y las de otra cuenta (van aparte, en `noUsables`).
 */
export function pasosPuestaEnMarcha(i: {
  modo: ModoTransporte; cuenta: CuentaPublica | null; saldoUSD: number; plantillasAprobadas: number;
  /** Del Resumen. Sin el dato (backend anterior, o no se supo) no hay último paso: no se inventa uno pendiente. */
  hayCampanaConfirmada?: boolean | null;
}): PasoPuestaEnMarcha[] {
  const c = i.cuenta;
  const pasos: PasoPuestaEnMarcha[] = [];
  // Parado en la marca de demostración, con el servidor en twilio, ningún paso de abajo se puede hacer:
  // «Conectá tu número» llevaba a un formulario que terminaba en MARCA_DEMO. Lo primero es elegir la marca
  // del cliente (el asistente de Conexión tiene el selector a mano). En simulado la demo hace todo.
  if (i.modo === "twilio" && c?.marcaDemoBloqueada) {
    pasos.push({ clave: "comercio", titulo: "Elegí la marca del cliente: estás en la de demostración, que no se conecta a un WhatsApp real", hecho: false, ruta: `${CONFIG}?seccion=cuenta` });
  }
  pasos.push({ clave: "cuenta", titulo: "Conectá tu número de WhatsApp", hecho: cuentaDelModo(c, i.modo), ruta: `${CONFIG}?seccion=cuenta`, deDeenex: true });
  if (i.modo === "twilio") {
    pasos.push(
      { clave: "webhooks", titulo: "Activá la recepción de respuestas y bajas", hecho: !!c?.webhooks?.inboundConfigurado, ruta: `${CONFIG}?seccion=cuenta`, deDeenex: true },
      { clave: "optout", titulo: "Confirmá que la baja con la palabra BAJA está activada", hecho: !!c?.config?.advancedOptOutConfirmado, ruta: `${CONFIG}?seccion=cuenta`, deDeenex: true },
    );
  }
  // El saldo va antes que la prueba: la prueba de la campaña se cobra como un mensaje y sin saldo el asistente la
  // corta. Antes el último paso era la plantilla y la lista desaparecía ahí: el comercio no tenía dicho que seguía
  // armar la campaña y mandarse la prueba, ni que la prueba también sale del saldo.
  pasos.push(
    { clave: "saldo", titulo: "Cargá saldo: la prueba y cada mensaje entregado se descuentan de ahí", hecho: Number(i.saldoUSD) > 0, ruta: `${CONFIG}?seccion=saldo` },
    { clave: "plantilla", titulo: "Creá una plantilla y esperá que Meta la apruebe", hecho: Number(i.plantillasAprobadas) > 0, ruta: `${R}/plantillas` },
  );
  if (typeof i.hayCampanaConfirmada === "boolean") {
    pasos.push({ clave: "campana", titulo: "Armá tu primera campaña: antes de confirmarla te mandás una prueba a tu teléfono", hecho: i.hayCampanaConfirmada, ruta: `${R}/campanas/nueva` });
  }
  return pasos;
}
